import { toSeconds } from '@art-suite/art-core-ts';

import { type ApiScanNote, getFlattenedScanNotes, isScanNoteExternal } from '../ApiSchemas/RecordTypes/ApiScanNoteSchema';

/**
 * Whether a note appears on an artifact (scope reports, PDFs and share links). Every artifact
 * applies this one rule through {@link createArtifactScanNotesLookup}.
 *
 * - Internal notes never appear, whatever their type (see `ScanNoteVisibilitySchema`).
 * - Remarks (`info`, `instructions`) appear when their body has text.
 * - Status notes (`approval`, `editRequest`, `editResolution`) always appear, even with an empty
 *   body: the decision itself is the content.
 * - `edit`, `delete` and the deprecated `editApproval` never appear.
 *
 * Callers apply edits and deletes to their parents first: the server through
 * `getDeletedParentScanNoteIds` on raw rows, clients by flattening with {@link getFlattenedScanNotes}.
 */
export const isScanNoteEligibleForScopeReportDocument = (
  scanNote: ApiScanNote,
): boolean => {
  const { scanNoteType } = scanNote;
  if (!isScanNoteExternal(scanNote)) return false;

  switch (scanNoteType) {
    case 'edit':
    case 'delete':
    case 'editApproval':
      return false;
    case 'approval':
    case 'editRequest':
    case 'editResolution':
      return true;
    case 'info':
    case 'instructions':
      return scanNote.body.trim() !== '';
    default: {
      const _exhaustive: never = scanNoteType;
      return _exhaustive;
    }
  }
};

const editWorkflowNotesByScanId = (
  notes: ApiScanNote[],
  scanNoteType: 'editRequest' | 'editResolution',
): Record<string, ApiScanNote[]> => {
  const notesByScanId: Record<string, ApiScanNote[]> = {};

  for (const note of notes) {
    if (note.scanNoteType !== scanNoteType) continue;
    (notesByScanId[note.scanId] ??= []).push(note);
  }

  for (const scanId of Object.keys(notesByScanId)) {
    notesByScanId[scanId].sort((left, right) => toSeconds(left.createdAt) - toSeconds(right.createdAt));
  }

  return notesByScanId;
};

const resolvedEditWorkflowNoteIds = (notes: ApiScanNote[]): Set<string> => {
  const editRequestsByScanId = editWorkflowNotesByScanId(notes, 'editRequest');
  const editResolutionsByScanId = editWorkflowNotesByScanId(notes, 'editResolution');
  const omitNoteIds = new Set<string>();
  const allScanIds = new Set([
    ...Object.keys(editRequestsByScanId),
    ...Object.keys(editResolutionsByScanId),
  ]);

  for (const scanId of allScanIds) {
    const editRequests = editRequestsByScanId[scanId] ?? [];
    const editResolutions = editResolutionsByScanId[scanId] ?? [];
    const resolvedPairCount = Math.min(editRequests.length, editResolutions.length);

    for (let index = 0; index < resolvedPairCount; index++) {
      omitNoteIds.add(editRequests[index].id);
      omitNoteIds.add(editResolutions[index].id);
    }
  }

  return omitNoteIds;
};

/**
 * Omit resolved edit-workflow notes per scanId by pairing oldest `editRequest` with oldest
 * `editResolution`. Surplus requests (e.g. a new unresolved edit on the same scan) remain visible.
 */
export const omitResolvedEditWorkflowNotes = (
  notesForScanPart: ApiScanNote[] | undefined,
): ApiScanNote[] => {
  const notes = notesForScanPart ?? [];
  const omitNoteIds = resolvedEditWorkflowNoteIds(notes);
  if (omitNoteIds.size === 0) return notes;
  return notes.filter(note => !omitNoteIds.has(note.id));
};

/**
 * Eligible notes for one scan part (`group.id`, `task.id`, `option.id`).
 *
 * Returns all document-eligible notes for the part, newest first. Grouped by `scanPartId` only —
 * not scoped by `scanId`. Resolved per-scanId edit workflow pairs are omitted; surplus requests remain.
 */
export const filterScanNotesForScopeReportPart = (
  notesForScanPart: ApiScanNote[] | undefined,
): ApiScanNote[] =>
  omitResolvedEditWorkflowNotes(notesForScanPart)
    .filter(isScanNoteEligibleForScopeReportDocument)
    .sort((left, right) => toSeconds(right.createdAt) - toSeconds(left.createdAt));

export type ArtifactScanNotesLookup = (scanPartId: string) => ApiScanNote[];

/**
 * The notes each scan part shows on an artifact, built from a scan's raw note list: its own notes
 * plus those carried over from the scans it was copied from. Every artifact gets its notes here:
 * the server's PDFs, HTML reports and share links (`createScanNotesLookup`), Renovator's previews
 * and artifact views, and KaiPro's scope report preview.
 *
 * Edits and deletes are applied first ({@link getFlattenedScanNotes}), then each part's notes go
 * through {@link filterScanNotesForScopeReportPart}, newest first. Internal notes are left out
 * here, when an artifact is built; reading notes never filters them.
 */
export const createArtifactScanNotesLookup = (scanNotes: readonly ApiScanNote[]): ArtifactScanNotesLookup => {
  const notesByScanPartId: Record<string, ApiScanNote[]> = {};
  for (const note of getFlattenedScanNotes(scanNotes)) {
    if (note.scanPartId) (notesByScanPartId[note.scanPartId] ??= []).push(note);
  }
  return scanPartId => filterScanNotesForScopeReportPart(notesByScanPartId[scanPartId]);
};
